import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Teacher {
  teacherId: string;
  name: string;
  email: string;
  phone: string;
  department: string;
  subject: string;
  qualification: string;
  experience: number;
  joiningDate: string;
  salary: number;
  address: string;
  emergencyContact: string;
  status: string;
}

interface UpdateTeacherDetailsProps {
  domain: string;
}

const UpdateTeacherDetails: React.FC<UpdateTeacherDetailsProps> = ({ domain }) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [teacherData, setTeacherData] = useState<Teacher | null>(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Load teachers list
  useEffect(() => {
    loadTeachers();
  }, [domain]);

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const q = query(teachersRef);
      const snapshot = await getDocs(q);

      const teachersList: Teacher[] = [];
      snapshot.forEach((doc) => {
        teachersList.push(doc.data() as Teacher);
      });

      teachersList.sort((a, b) => a.name.localeCompare(b.name));
      setTeachers(teachersList);
    } catch (error) {
      console.error('Error loading teachers:', error);
      alert('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const loadTeacherDetails = async (teacherId: string) => {
    if (!teacherId) return;

    setLoading(true);
    try {
      const docId = teacherId.replace(/\s+/g, '_');
      const docRef = doc(db, `school-users/${domain}/teachers`, docId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setTeacherData(docSnap.data() as Teacher);
        setUpdateSuccess(false);
      } else {
        alert('Teacher not found');
      }
    } catch (error) {
      console.error('Error loading teacher details:', error);
      alert('Failed to load teacher details');
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const teacherId = e.target.value;
    setSelectedTeacherId(teacherId);
    if (teacherId) {
      loadTeacherDetails(teacherId);
    } else {
      setTeacherData(null);
    }
  };

  const handleInputChange = (field: keyof Teacher, value: string | number) => {
    if (teacherData) {
      setTeacherData({
        ...teacherData,
        [field]: value,
      });
    }
  };

  const handleUpdate = async () => {
    if (!teacherData) return;

    setUpdating(true);
    setUpdateSuccess(false);

    try {
      const docId = teacherData.teacherId.replace(/\s+/g, '_');
      const docRef = doc(db, `school-users/${domain}/teachers`, docId);

      const updateData = {
        ...teacherData,
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(docRef, updateData);

      setUpdateSuccess(true);
      console.log('✅ Teacher details updated successfully');

      // Reload teachers list
      await loadTeachers();
    } catch (error: any) {
      console.error('Error updating teacher:', error);
      alert(`Failed to update teacher: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-pencil-square me-2 text-primary-custom"></i>
          Update Teacher Details
        </h5>
        <small className="text-muted">Search and update teacher information</small>
      </div>
      <div className="card-body">
        {/* Teacher Selection */}
        <div className="mb-4">
          <label className="form-label fw-semibold">
            <i className="bi bi-search me-2"></i>
            Select Teacher
          </label>
          <select
            className="form-select"
            value={selectedTeacherId}
            onChange={handleTeacherSelect}
            disabled={loading}
          >
            <option value="">-- Select a teacher --</option>
            {teachers.map((teacher) => (
              <option key={teacher.teacherId} value={teacher.teacherId}>
                {teacher.name} ({teacher.teacherId}) - {teacher.department}
              </option>
            ))}
          </select>
          {loading && (
            <small className="text-muted d-block mt-2">
              <span className="spinner-border spinner-border-sm me-2"></span>
              Loading...
            </small>
          )}
        </div>

        {/* Teacher Details Form */}
        {teacherData && (
          <div>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label">Teacher ID</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.teacherId}
                  disabled
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-control"
                  value={teacherData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone *</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Department</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Subject</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Qualification</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.qualification}
                  onChange={(e) => handleInputChange('qualification', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Experience (Years)</label>
                <input
                  type="number"
                  className="form-control"
                  value={teacherData.experience}
                  onChange={(e) => handleInputChange('experience', Number(e.target.value))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Joining Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={teacherData.joiningDate}
                  onChange={(e) => handleInputChange('joiningDate', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Salary</label>
                <input
                  type="number"
                  className="form-control"
                  value={teacherData.salary}
                  onChange={(e) => handleInputChange('salary', Number(e.target.value))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Emergency Contact</label>
                <input
                  type="text"
                  className="form-control"
                  value={teacherData.emergencyContact}
                  onChange={(e) => handleInputChange('emergencyContact', e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={teacherData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="on-leave">On Leave</option>
                </select>
              </div>
              <div className="col-12">
                <label className="form-label">Address</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={teacherData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              </div>
            </div>

            {/* Success Message */}
            {updateSuccess && (
              <div className="alert alert-success">
                <i className="bi bi-check-circle me-2"></i>
                Teacher details updated successfully!
              </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={handleUpdate}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-save me-2"></i>
                    Update Details
                  </>
                )}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => loadTeacherDetails(selectedTeacherId)}
                disabled={updating}
              >
                <i className="bi bi-arrow-counterclockwise me-2"></i>
                Reset Changes
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!teacherData && !loading && teachers.length > 0 && (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-person-plus fs-1 d-block mb-3"></i>
            <p>Select a teacher from the dropdown to update their details</p>
          </div>
        )}

        {/* No Teachers */}
        {!loading && teachers.length === 0 && (
          <div className="alert alert-warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            No teachers found. Please upload teachers first using Bulk Upload.
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateTeacherDetails;
